import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeader, useAuth } from "@/context/AuthContext";
import { Plus, Search, X, CheckSquare, Trash2, Edit2, Save } from "lucide-react";

const PRIORIDADES = ["Normal","Urgente"];

export default function TarefasList() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "administracao";

  const [tarefas, setTarefas] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [viewMode, setViewMode] = useState("meu");
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ descricao:"", data:"", prioridade:"Normal", concluida:"Não", atribuida:[], processo_id:"", numero_processo:"", nome_cliente:"" });
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(async () => {
    setLoading(true);
    const params = { search, concluida: filter, skip: 0, limit: 50 };
    if (isAdmin && viewMode === "meu") params.meu = true;
    const { data } = await axios.get(`${API}/tarefas`, { params, headers: getAuthHeader() });
    setTarefas(data.tarefas); setTotal(data.total); setLoading(false);
  }, [search, filter, viewMode, isAdmin]);

  useEffect(() => { load(); }, [load]);
  // Carrega utilizadores uma única vez (referências de módulo estáveis não precisam de deps)
  useEffect(() => { axios.get(`${API}/utilizadores`, { headers: getAuthHeader() }).then(r => setUsers(r.data)).catch(()=>{}); }, []); // eslint-disable-next-line react-hooks/exhaustive-deps

  const openNew = () => { setEditId(null); setForm({ descricao:"",data:"",prioridade:"Normal",concluida:"Não",atribuida:[],processo_id:"",numero_processo:"",nome_cliente:"" }); setShowModal(true); };
  const openEdit = (t) => { setEditId(t.id); setForm({ descricao:t.descricao||"",data:t.data||"",prioridade:t.prioridade||"Normal",concluida:t.concluida||"Não",atribuida:t.atribuida||[],processo_id:t.processo_id||"",numero_processo:t.numero_processo||"",nome_cliente:t.nome_cliente||"" }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editId) { await axios.put(`${API}/tarefas/${editId}`, form, { headers: getAuthHeader() }); }
      else { await axios.post(`${API}/tarefas`, form, { headers: getAuthHeader() }); }
      setShowModal(false); load();
    } catch(err) { alert(err.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const handleToggle = async (t) => {
    await axios.put(`${API}/tarefas/${t.id}`, { concluida: t.concluida==="Sim" ? "Não" : "Sim" }, { headers: getAuthHeader() });
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar tarefa?")) return;
    await axios.delete(`${API}/tarefas/${id}`, { headers: getAuthHeader() });
    load();
  };

  const getUserName = (uid) => { const u = users.find(x=>x.id===uid); return u ? `${u.nome} ${u.apelido}` : uid; };

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CheckSquare size={22} className="text-[#0b679e]"/>
            {isAdmin && viewMode === "todas" ? "Todas as Tarefas" : "Minhas Tarefas"}
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">{total} tarefa(s)</p>
        </div>
        <button onClick={openNew} data-testid="nova-tarefa-btn"
          className="flex items-center gap-2 bg-[#0b679e] hover:bg-[#084e78] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Plus size={16}/> Nova Tarefa
        </button>
      </div>

      {/* Tabs for admin/administração */}
      {isAdmin && (
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
          {[["meu","Minhas Tarefas"],["todas","Todas as Tarefas"]].map(([mode, label]) => (
            <button key={mode} onClick={() => setViewMode(mode)} data-testid={`tab-${mode}`}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === mode ? "bg-white text-[#0b679e] shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={search} onChange={e=>{setSearch(e.target.value);}} placeholder="Pesquisar tarefas..."
            data-testid="search-tarefas"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/>
        </div>
        <div className="flex gap-2">
          {[["","Todas"],["Não","Pendentes"],["Sim","Concluídas"]].map(([v,l])=>(
            <button key={v} onClick={()=>setFilter(v)} data-testid={`filter-${l.toLowerCase()}`}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${filter===v?"bg-[#0b679e] text-white":"border border-slate-300 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* Tasks */}
      <div className="bg-white rounded-xl border border-slate-200">
        {(() => {
          if (loading) return (
            <div className="flex items-center justify-center h-40"><div className="w-7 h-7 border-4 border-[#0b679e] border-t-[#F5A623] rounded-full animate-spin"/></div>
          );
          if (tarefas.length === 0) return (
            <div className="text-center py-16 text-slate-400"><CheckSquare size={40} className="mx-auto mb-3 opacity-30"/><p>Sem tarefas</p></div>
          );
          return (
            <div className="divide-y divide-slate-100">
              {tarefas.map(t => (
              <div key={t.id} data-testid={`tarefa-row-${t.id}`}
                className={`flex items-start gap-4 px-5 py-4 hover:bg-slate-50 transition-colors ${t.concluida==="Sim" ? "opacity-60" : ""}`}>
                <button onClick={()=>handleToggle(t)} data-testid={`toggle-tarefa-${t.id}`}
                  className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${t.concluida==="Sim"?"border-emerald-500 bg-emerald-500 text-white":"border-slate-300 hover:border-[#0b679e]"}`}>
                  {t.concluida==="Sim" && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm text-slate-900 ${t.concluida==="Sim" ? "line-through text-slate-400" : ""}`}>{t.descricao || "—"}</p>
                  <div className="flex items-center flex-wrap gap-2 mt-1">
                    {t.data && <span className="text-xs text-slate-400">{t.data}</span>}
                    {t.numero_processo && <span className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">Proc. {t.numero_processo}</span>}
                    {(t.atribuida||[]).map(uid=><span key={uid} className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{getUserName(uid)}</span>)}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {t.prioridade==="Urgente" && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">Urgente</span>}
                  <button onClick={()=>openEdit(t)} className="text-slate-400 hover:text-slate-600 p-1"><Edit2 size={14}/></button>
                  <button onClick={()=>handleDelete(t.id)} className="text-slate-400 hover:text-red-500 p-1"><Trash2 size={14}/></button>
                </div>
              </div>
            ))}
            </div>
          );
        })()}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">{editId ? "Editar Tarefa" : "Nova Tarefa"}</h3>
              <button onClick={()=>setShowModal(false)}><X size={18}/></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Descrição *</label>
                <textarea value={form.descricao} onChange={e=>setForm({...form,descricao:e.target.value})} required rows={3}
                  data-testid="tarefa-descricao"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Data</label>
                  <input type="date" value={form.data} onChange={e=>setForm({...form,data:e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Prioridade</label>
                  <select value={form.prioridade} onChange={e=>setForm({...form,prioridade:e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]">
                    {PRIORIDADES.map(p=><option key={p}>{p}</option>)}
                  </select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Concluída?</label>
                  <select value={form.concluida} onChange={e=>setForm({...form,concluida:e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]">
                    <option>Não</option><option>Sim</option>
                  </select></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Nº Processo</label>
                  <input value={form.numero_processo} onChange={e=>setForm({...form,numero_processo:e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/></div>
              </div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Atribuída a</label>
                <div className="border border-slate-300 rounded-lg p-2 max-h-32 overflow-y-auto space-y-1">
                  {users.map(u=><label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={(form.atribuida||[]).includes(u.id)}
                      onChange={e=>setForm({...form,atribuida:e.target.checked?[...(form.atribuida||[]),u.id]:(form.atribuida||[]).filter(x=>x!==u.id)})} className="rounded"/>
                    {u.nome} {u.apelido}
                  </label>)}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
                <button type="button" onClick={()=>setShowModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg text-sm">Cancelar</button>
                <button type="submit" disabled={saving} data-testid="save-tarefa-btn"
                  className="px-4 py-2 bg-[#0b679e] hover:bg-[#084e78] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center gap-2">
                  {saving&&<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>}
                  {editId ? "Atualizar" : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
